import { useSEO } from "../lib/useSEO";
import PageHero from "../components/layout/PageHero";
import SectionHeader from "../components/SectionHeader";
import CTASection from "../components/CTASection";
import Icon from "../components/Icon";
import { Reveal } from "../components/motion/Reveal";
import { VALUES, HOME_SERVICES } from "../data/static";

const VISION_IMG = "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=1200&q=80";

export default function About() {
  useSEO(
    "About Us — Suvera Academy",
    "Suvera Academy is a student-focused international education consultancy providing personalised guidance, transparent information and end-to-end support."
  );
  return (
    <main data-testid="about-page">
      <PageHero
        eyebrow="About Suvera Academy"
        title="Building Better Global Education Journeys"
        description="A student-focused international education consultancy built on honest advice, deep destination knowledge and end-to-end support."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "About Us" }]}
        testid="about-hero"
      />

      <section className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <SectionHeader
              chapter="Who We Are"
              eyebrow="Introduction"
              title="A Consultancy Built Around Students"
              description="Suvera Academy is an international education consultancy helping students discover suitable study-abroad opportunities, universities, academic pathways, scholarships and career-oriented education options. We exist to make complex international decisions feel clear, structured and achievable."
            />
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="img-frame">
                <img src={VISION_IMG} alt="Students studying in a university library" className="max-h-[60vh] w-full object-cover lg:max-h-[420px]" loading="lazy" />
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl bg-navy p-8 text-white sm:p-10" data-testid="vision-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10"><Icon name="Binoculars" className="h-6 w-6" /></span>
              <h3 className="mt-6 font-display text-2xl font-semibold">Our Vision</h3>
              <p className="mt-3 leading-relaxed text-white/70">
                Help students access meaningful international education opportunities with clarity and confidence.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-8 sm:p-10" data-testid="mission-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy"><Icon name="Target" className="h-6 w-6" /></span>
              <h3 className="mt-6 font-display text-2xl font-semibold text-navy-dark">Our Mission</h3>
              <p className="mt-3 leading-relaxed text-slate-600">
                Provide personalised guidance, transparent information and end-to-end support at every stage of the study-abroad journey.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-sand">
        <div className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
          <SectionHeader chapter="Principles" eyebrow="Our Values" title="The Values Behind Every Recommendation" />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 3) * 0.08} className="h-full">
                <div className="group h-full rounded-2xl border border-slate-200 bg-white p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/30 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]" data-testid={`value-card-${v.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors duration-300 group-hover:bg-navy group-hover:text-white">
                    <Icon name={v.icon} className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-display text-xl font-semibold text-navy-dark">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
        <SectionHeader
          chapter="What We Do"
          eyebrow="Our Services"
          title="End-to-End Support, One Team"
          description="Counselling, course selection, institution selection, applications, scholarships, visa guidance, pre-departure support and post-arrival guidance."
        />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {HOME_SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 4) * 0.06} className="h-full">
              <div className="flex h-full items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-navy/5 text-navy">
                  <Icon name={s.icon} className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-navy-dark">{s.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{s.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection title="Talk to an Advisor" description="Have questions about studying abroad? Speak with a Suvera Academy education advisor — the first consultation is free." />
    </main>
  );
}
